
import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { BarChart, Calendar, Award } from 'lucide-react';
import { useFastingHistory } from '@/contexts/fasting/FastingHistoryContext';
import { formatTime } from '@/lib/formatTime';
import { AnalyticsModal } from '@/components/progress/AnalyticsModal';

export const ProgressPage: React.FC = () => {
  const [analyticsOpen, setAnalyticsOpen] = useState(false);
  const { 
    sessions, 
    getTotalFasts, 
    getLongestFast, 
    getCurrentStreak,
    getRecentSessions
  } = useFastingHistory();
  
  const totalFasts = getTotalFasts();
  const longestFast = getLongestFast();
  const currentStreak = getCurrentStreak();
  const recentHistory = getRecentSessions(5);
  
  return (
    <div>
      <h1 className="text-2xl font-bold mb-6 text-center">Your Progress</h1>
      
      <div className="grid grid-cols-3 gap-4 mb-6">
        <Card className="bg-gradient-to-br from-white to-fasttrack-light-purple/30">
          <CardContent className="p-4 text-center">
            <div className="text-3xl font-bold text-foreground">{totalFasts}</div>
            <p className="text-xs text-muted-foreground">Total Fasts</p>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-white to-fasttrack-light-blue/30">
          <CardContent className="p-4 text-center">
            <div className="text-3xl font-bold text-foreground">{longestFast}h</div>
            <p className="text-xs text-muted-foreground">Longest Fast</p>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-white to-fasttrack-light-purple/30">
          <CardContent className="p-4 text-center">
            <div className="text-3xl font-bold text-foreground">{currentStreak}</div>
            <p className="text-xs text-muted-foreground">Current Streak</p>
          </CardContent>
        </Card>
      </div>
      
      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="flex items-center text-lg">
            <Calendar size={18} className="mr-2" />
            Fast History
          </CardTitle>
        </CardHeader>
        <CardContent>
          {recentHistory.length > 0 ? (
            <div className="space-y-3">
              {recentHistory.map((fast) => (
                <div key={fast.id} className="flex justify-between items-center py-2 border-b last:border-b-0">
                  <div>
                    <div className="font-medium">{formatDate(fast.date)}</div>
                    <div className="text-xs text-muted-foreground">
                      {Math.round(fast.duration / 3600)} hour fast ({fast.completed ? 'Completed' : 'Abandoned'})
                    </div>
                  </div>
                  {fast.completed && (
                    <div className="h-6 w-6 rounded-full bg-green-100 flex items-center justify-center">
                      <Award size={14} className="text-green-600" />
                    </div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center text-muted-foreground py-6">
              No fasting history yet. Start a fast to track your progress!
            </div>
          )}
        </CardContent>
      </Card>
      
      <Button 
        variant="outline" 
        className="w-full" 
        onClick={() => setAnalyticsOpen(true)}
      >
        <BarChart size={18} className="mr-2" />
        View Detailed Analytics
      </Button>
      
      <AnalyticsModal 
        open={analyticsOpen}
        onOpenChange={setAnalyticsOpen}
      />
      
      <div className="text-center text-xs text-muted-foreground mt-8 mb-4">
        Designed by <a href="https://wyattsanders.com" target="_blank" rel="noopener noreferrer" className="text-fasttrack-blue hover:underline">wyattsanders.com</a>
      </div>
    </div>
  );
};

// Helper function to format dates
const formatDate = (dateString: string) => {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};
